import { createStore, createHook, createActionsHook, createStateHook } from 'react-sweet-state';

export type ParentContent = {
	id: string;
	title: string;
	contentType: string;
};

export type Content = {
	id: string;
	type: string;
	subType?: 'live' | null;
};

export type SubmitCallback = (props: { baseUrl: string; path: string }) => Promise<void>;

export type OnCloseCallback = () => void;

export type OutcomeType = 'liveDoc' | 'whiteboard' | 'database' | 'slides';

type State = {
	showSmartCreationModal: boolean;
	spaceKey: string;
	parentContent: ParentContent;
	content?: Content;
	stagingAreaActive: boolean;
	isFloating: boolean;
	shouldShowBackButton?: boolean;
	entryPoint: string;
	initialOutcomeType?: OutcomeType;
	submitCallback?: SubmitCallback;
	onCloseCallback?: OnCloseCallback;
};

const initialState: State = {
	showSmartCreationModal: false,
	spaceKey: '',
	parentContent: {
		id: '',
		title: '',
		contentType: '',
	},
	content: undefined,
	stagingAreaActive: false,
	isFloating: false,
	shouldShowBackButton: undefined,
	entryPoint: 'confluenceUnknown',
	initialOutcomeType: undefined,
	submitCallback: undefined,
	onCloseCallback: undefined,
};

export type OpenModalParams = {
	entryPoint: string;
	spaceKey?: string;
	parentContent?: ParentContent;
	stagingAreaActive?: boolean;
	isFloating?: boolean;
	shouldShowBackButton?: boolean;
	content?: Content;
	initialOutcomeType?: OutcomeType;
	submitCallback?: SubmitCallback;
	onCloseCallback?: OnCloseCallback;
};

export const actions = {
	openModal:
		({
			entryPoint,
			spaceKey,
			parentContent,
			stagingAreaActive,
			isFloating,
			shouldShowBackButton,
			content,
			initialOutcomeType,
			submitCallback,
			onCloseCallback,
		}: OpenModalParams) =>
		({ setState }: { setState: (state: Partial<State>) => void }) => {
			setState({
				showSmartCreationModal: true,
				entryPoint,
				spaceKey,
				parentContent: parentContent ?? {
					id: '',
					title: '',
					contentType: '',
				},
				content: content ?? undefined,
				stagingAreaActive: stagingAreaActive ?? false,
				isFloating: isFloating ?? false,
				shouldShowBackButton: shouldShowBackButton ?? undefined,
				initialOutcomeType: initialOutcomeType ?? undefined,
				submitCallback: submitCallback ?? undefined,
				onCloseCallback: onCloseCallback ?? undefined,
			});
		},
	closeModal:
		() =>
		({ setState }: { setState: (state: Partial<State>) => void }) => {
			setState({
				showSmartCreationModal: false,
				content: undefined,
				stagingAreaActive: false,
				isFloating: false,
				shouldShowBackButton: undefined,
				entryPoint: 'confluenceUnknown',
				initialOutcomeType: undefined,
				onCloseCallback: undefined,
			});
		},
};

export const Store = createStore({
	initialState,
	actions,
	name: 'smartCreationModalStore',
});

export const useSmartCreationModalStore = createHook(Store);

export const useSmartCreationModalActions = createActionsHook(Store);
export const useSmartCreationModalState = createStateHook(Store);
